import { useState, useEffect } from 'react';
import { cn, formatCurrency } from '@/utils';
import type { IDVInfo } from '@/types/integrations';

interface IDVSliderProps {
  idv: IDVInfo;
  value: number;
  onChange: (value: number) => void;
}

function formatINR(amount: number) {
  return formatCurrency(amount);
}

export function IDVSlider({ idv, value, onChange }: IDVSliderProps) {
  const [inputVal, setInputVal] = useState(String(value));

  useEffect(() => {
    setInputVal(String(value));
  }, [value]);

  function handleSlider(e: React.ChangeEvent<HTMLInputElement>) {
    const next = Number(e.target.value);
    onChange(next);
    setInputVal(String(next));
  }

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    setInputVal(e.target.value);
  }

  function handleInputBlur() {
    const next = Math.min(idv.max_idv, Math.max(idv.min_idv, Number(inputVal) || idv.default_idv));
    onChange(next);
    setInputVal(String(next));
  }

  const pct = idv.max_idv === idv.min_idv
    ? 100
    : Math.round(((value - idv.min_idv) / (idv.max_idv - idv.min_idv)) * 100);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Insured Declared Value (IDV)
        </span>
        {value === idv.default_idv && (
          <span className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
            Default
          </span>
        )}
        {value !== idv.default_idv && value === idv.idv && (
          <span className="inline-flex items-center rounded-full bg-green-50 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700">
            Recommended
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input
          type="range"
          min={idv.min_idv}
          max={idv.max_idv}
          step={1000}
          value={value}
          onChange={handleSlider}
          className="flex-1 h-2 rounded-full appearance-none cursor-pointer accent-primary-600"
          style={{
            background: `linear-gradient(to right, var(--tw-color-primary-600, #4f46e5) ${pct}%, #e5e7eb ${pct}%)`,
          }}
        />
        <input
          type="number"
          value={inputVal}
          onChange={handleInput}
          onBlur={handleInputBlur}
          min={idv.min_idv}
          max={idv.max_idv}
          className={cn(
            'w-28 px-2 py-1.5 text-sm border rounded-lg text-right tabular-nums',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
          )}
        />
      </div>

      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>Min: {formatINR(idv.min_idv)}</span>
        <span className="font-medium text-gray-700 dark:text-gray-300">{formatINR(value)}</span>
        <span>Max: {formatINR(idv.max_idv)}</span>
      </div>
    </div>
  );
}
